import ConfirmDialog from '@/components/ConfirmDialog';
import { useToast } from '@/hooks/useToast';
import { broadcastsService } from '@/services/broadcastsService';
import { CATEGORY_COLORS } from '@/types/templates';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

/**
 * Status badge component
 */
const StatusBadge = ({ status }) => {
  const statusConfig = {
    draft: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Draft' },
    scheduled: {
      bg: 'bg-primary-100',
      text: 'text-primary-800',
      label: 'Scheduled',
    },
    in_progress: {
      bg: 'bg-yellow-100',
      text: 'text-yellow-800',
      label: 'In Progress',
    },
    completed: {
      bg: 'bg-green-100',
      text: 'text-green-800',
      label: 'Completed',
    },
    failed: { bg: 'bg-red-100', text: 'text-red-800', label: 'Failed' },
    cancelled: {
      bg: 'bg-gray-100',
      text: 'text-gray-800',
      label: 'Cancelled',
    },
    paused: {
      bg: 'bg-orange-100',
      text: 'text-orange-800',
      label: 'Paused',
    },
  };

  const config = statusConfig[status] || statusConfig.draft;

  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold shadow-sm border border-black/5 ${config.bg} ${config.text}`}
    >
      {config.label}
    </span>
  );
};

/**
 * Recipient Status Badge component for message-specific statuses
 */
const RecipientStatusBadge = ({ status }) => {
  const statusConfig = {
    sent: { bg: 'bg-primary-100', text: 'text-primary-800', label: 'Sent' },
    delivered: {
      bg: 'bg-green-100',
      text: 'text-green-800',
      label: 'Delivered',
    },
    read: { bg: 'bg-purple-100', text: 'text-purple-800', label: 'Read' },
    failed: { bg: 'bg-red-100', text: 'text-red-800', label: 'Failed' },
    pending: { bg: 'bg-gray-100', text: 'text-gray-800', label: 'Pending' },
  };

  const config = statusConfig[status] || {
    bg: 'bg-gray-100',
    text: 'text-gray-800',
    label: status || 'Unknown',
  };

  return (
    <span
      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.bg} ${config.text}`}
    >
      {config.label}
    </span>
  );
};

/**
 * Stat Card component
 */
const StatCard = ({ title, value, percentage, icon, color = 'blue' }) => {
  const colorClasses = {
    primary: 'bg-primary-100 text-primary-600',
    green: 'bg-green-100 text-green-600',
    yellow: 'bg-yellow-100 text-yellow-600',
    red: 'bg-red-100 text-red-600',
    purple: 'bg-purple-100 text-purple-600',
  };

  return (
    <div className="bg-white rounded-lg shadow p-6">
      <div className="flex items-center justify-between">
        <div className="flex-1">
          <p className="text-sm font-medium text-gray-600">{title}</p>
          <div className="mt-2 flex items-baseline">
            <p className="text-2xl font-semibold text-gray-900">{value}</p>
            {percentage !== undefined && (
              <p className="ml-2 text-sm text-gray-600">({percentage}%)</p>
            )}
          </div>
        </div>
        {icon && (
          <div className={`p-3 rounded-full ${colorClasses[color]}`}>
            {icon}
          </div>
        )}
      </div>
    </div>
  );
};

/**
 * BroadcastDetail page component
 */
const BroadcastDetail = () => {
  const { projectId, broadcastId } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useToast();
  const queryClient = useQueryClient();

  // State
  const [activeTab, setActiveTab] = useState('overview');
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [showPauseDialog, setShowPauseDialog] = useState(false);
  const [showResumeDialog, setShowResumeDialog] = useState(false);
  const [recipientPage, setRecipientPage] = useState(1);
  const recipientLimit = 20;

  // Recipients filter state
  const [recipientStatusFilter, setRecipientStatusFilter] = useState('');
  const [recipientPricingFilter, setRecipientPricingFilter] = useState('');
  const [isExporting, setIsExporting] = useState(false);

  const handleFilterChange = setter => e => {
    setter(e.target.value);
    setRecipientPage(1);
  };

  // Fetch broadcast details
  const {
    data: broadcastResponse,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['broadcast', broadcastId],
    queryFn: () => broadcastsService.getBroadcast(broadcastId),
    enabled: !!broadcastId,
    refetchInterval: data => {
      const broadcastData = data?.data || data;
      // Auto-refresh every 10 seconds if in progress
      return broadcastData?.status === 'in_progress' ? 10000 : false;
    },
  });

  // Extract broadcast data
  const broadcast = broadcastResponse?.data || broadcastResponse;

  // Fetch recipients
  const {
    data: recipientsResponse,
    isLoading: recipientsLoading,
    refetch: refetchRecipients,
  } = useQuery({
    queryKey: [
      'broadcast-recipients',
      broadcastId,
      recipientPage,
      recipientStatusFilter,
      recipientPricingFilter,
    ],
    queryFn: () =>
      broadcastsService.getBroadcastRecipients(broadcastId, {
        page: recipientPage,
        limit: recipientLimit,
        status: recipientStatusFilter || undefined,
        pricingType: recipientPricingFilter || undefined,
      }),
    enabled: activeTab === 'recipients' && !!broadcastId,
  });

  // Extract recipients and pagination
  const recipientsData = recipientsResponse?.data || recipientsResponse || {};
  const recipients = recipientsData.docs || [];
  const recipientsPagination = recipientsData.pagination || {
    total: recipientsData.totalDocs || recipientsData.total || 0,
    totalPages: recipientsData.totalPages || 0,
    page: recipientsData.page || 1,
  };

  // Analytics data is now part of the main broadcast response
  const analyticsLoading = false;

  // Pause mutation
  const pauseMutation = useMutation({
    mutationFn: () => broadcastsService.pauseBroadcast(broadcastId),
    onSuccess: () => {
      showSuccess('Broadcast paused successfully');
      queryClient.invalidateQueries(['broadcast', broadcastId]);
      setShowPauseDialog(false);
    },
    onError: error => {
      showError(error.response?.data?.message || 'Failed to pause broadcast');
    },
  });

  // Resume mutation
  const resumeMutation = useMutation({
    mutationFn: () => broadcastsService.resumeBroadcast(broadcastId),
    onSuccess: () => {
      showSuccess('Broadcast resumed successfully');
      queryClient.invalidateQueries(['broadcast', broadcastId]);
      setShowResumeDialog(false);
    },
    onError: error => {
      showError(error.response?.data?.message || 'Failed to resume broadcast');
    },
  });

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: () => broadcastsService.deleteBroadcast(broadcastId),
    onSuccess: () => {
      showSuccess('Broadcast deleted successfully');
      navigate(`/projects/${projectId}/broadcasts`);
    },
    onError: error => {
      showError(error.response?.data?.message || 'Failed to delete broadcast');
    },
  });

  // Format date
  const formatDate = dateString => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    });
  };

  // Calculate percentage
  const calculatePercentage = (value, total) => {
    if (!total || total === 0) return 0;
    return ((value / total) * 100).toFixed(1);
  };

  if (isLoading) {
    return (
      <div className="h-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded w-1/4 mb-4"></div>
          <div className="h-4 bg-gray-200 rounded w-1/2 mb-8"></div>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            {[1, 2, 3, 4].map(i => (
              <div key={i} className="h-32 bg-gray-200 rounded"></div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error || !broadcast) {
    return (
      <div className="h-full px-4 sm:px-6 lg:px-8 py-8">
        <div className="text-center">
          <h3 className="text-lg font-medium text-gray-900">
            Broadcast not found
          </h3>
          <p className="mt-2 text-sm text-gray-500">
            The broadcast you're looking for doesn't exist or has been deleted.
          </p>
          <button
            onClick={() => navigate(`/projects/${projectId}/broadcasts`)}
            className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
          >
            Back to Broadcasts
          </button>
        </div>
      </div>
    );
  }

  const stats = broadcast.statistics || {};
  const totalRecipients = broadcast.recipientCount || 0;

  return (
    <div className="h-full px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate(`/projects/${projectId}/broadcasts`)}
          className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-4"
        >
          <svg
            className="mr-2 h-4 w-4"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M15 19l-7-7 7-7"
            />
          </svg>
          Back to Broadcasts
        </button>

        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <h1 className="text-2xl font-semibold text-gray-900">
              {broadcast.name}
            </h1>
            <StatusBadge status={broadcast.status} />
          </div>

          <div className="flex items-center space-x-3">
            <button
              onClick={() => refetch()}
              className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <svg
                className="h-4 w-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Refresh
            </button>

            {broadcast.status === 'in_progress' && (
              <button
                onClick={() => setShowPauseDialog(true)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
              >
                Pause
              </button>
            )}

            {broadcast.status === 'paused' && (
              <button
                onClick={() => setShowResumeDialog(true)}
                className="inline-flex items-center px-3 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700"
              >
                Resume
              </button>
            )}

            {broadcast.status === 'scheduled' && (
              <button
                onClick={() => setShowDeleteDialog(true)}
                className="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
              >
                Delete
              </button>
            )}
          </div>
        </div>

        {broadcast.description && (
          <p className="mt-2 text-sm text-gray-600">{broadcast.description}</p>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 mb-6">
        <nav className="-mb-px flex space-x-8">
          {['overview', 'recipients', 'analytics'].map(tab => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`${
                activeTab === tab
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              } whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm capitalize`}
            >
              {tab}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="space-y-6">
          {/* Statistics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            <StatCard
              title="Total Recipients"
              value={totalRecipients.toLocaleString()}
              icon={
                <svg
                  className="h-6 w-6"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M9 6a3 3 0 11-6 0 3 3 0 016 0zM17 6a3 3 0 11-6 0 3 3 0 016 0zM12.93 17c.046-.327.07-.66.07-1a6.97 6.97 0 00-1.5-4.33A5 5 0 0119 16v1h-6.07zM6 11a5 5 0 015 5v1H1v-1a5 5 0 015-5z" />
                </svg>
              }
              color="blue"
            />

            <StatCard
              title="Sent"
              value={(stats.sent || 0).toLocaleString()}
              percentage={calculatePercentage(stats.sent, totalRecipients)}
              icon={
                <svg
                  className="h-6 w-6"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M10.894 2.553a1 1 0 00-1.788 0l-7 14a1 1 0 001.169 1.409l5-1.429A1 1 0 009 15.571V11a1 1 0 112 0v4.571a1 1 0 00.725.962l5 1.428a1 1 0 001.17-1.408l-7-14z" />
                </svg>
              }
              color="blue"
            />

            <StatCard
              title="Delivered"
              value={(stats.delivered || 0).toLocaleString()}
              percentage={calculatePercentage(stats.delivered, totalRecipients)}
              icon={
                <svg
                  className="h-6 w-6"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                    clipRule="evenodd"
                  />
                </svg>
              }
              color="green"
            />

            <StatCard
              title="Failed"
              value={(stats.failed || 0).toLocaleString()}
              percentage={calculatePercentage(stats.failed, totalRecipients)}
              icon={
                <svg
                  className="h-6 w-6"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
              }
              color="red"
            />

            <StatCard
              title="Read"
              value={(stats.read || 0).toLocaleString()}
              percentage={calculatePercentage(stats.read, stats.delivered)}
              icon={
                <svg
                  className="h-6 w-6"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path d="M10 12a2 2 0 100-4 2 2 0 000 4z" />
                  <path
                    fillRule="evenodd"
                    d="M.458 10C1.732 5.943 5.522 3 10 3s8.268 2.943 9.542 7c-1.274 4.057-5.064 7-9.542 7S1.732 14.057.458 10zM14 10a4 4 0 11-8 0 4 4 0 018 0z"
                    clipRule="evenodd"
                  />
                </svg>
              }
              color="purple"
            />

            <StatCard
              title="Replied"
              value={(stats.replied || 0).toLocaleString()}
              percentage={calculatePercentage(stats.replied, stats.delivered)}
              icon={
                <svg
                  className="h-6 w-6"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z"
                    clipRule="evenodd"
                  />
                </svg>
              }
              color="green"
            />

            <StatCard
              title="Pending"
              value={(stats.pending || 0).toLocaleString()}
              icon={
                <svg
                  className="h-6 w-6"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
                    clipRule="evenodd"
                  />
                </svg>
              }
              color="yellow"
            />
          </div>

          {/* Progress Bar */}
          {broadcast.status === 'in_progress' && (
            <div className="bg-white rounded-lg shadow p-6">
              <h3 className="text-sm font-medium text-gray-900 mb-3">
                Progress
              </h3>
              <div className="relative">
                <div className="overflow-hidden h-4 text-xs flex rounded bg-gray-200">
                  <div
                    style={{
                      width: `${calculatePercentage(stats.sent, totalRecipients)}%`,
                    }}
                    className="shadow-none flex flex-col text-center whitespace-nowrap text-white justify-center bg-primary-600 transition-all duration-500"
                  />
                </div>
                <p className="mt-2 text-sm text-gray-600">
                  {stats.sent || 0} of {totalRecipients} messages sent (
                  {calculatePercentage(stats.sent, totalRecipients)}%)
                </p>
              </div>
            </div>
          )}

          {/* Broadcast Information */}
          <div className="bg-white rounded-lg shadow overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200">
              <h3 className="text-lg font-medium text-gray-900">
                Broadcast Information
              </h3>
            </div>
            <div className="px-6 py-4">
              <dl className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-4">
                <div>
                  <dt className="text-sm font-medium text-gray-500">Name</dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {broadcast.name}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">Status</dt>
                  <dd className="mt-1">
                    <StatusBadge status={broadcast.status} />
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Execution Type
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 capitalize">
                    {broadcast.executionType || '-'}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Template
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {broadcast.template?.name ||
                      broadcast.templateName ||
                      broadcast.templateId?.name ||
                      '-'}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Category
                  </dt>
                  <dd className="mt-1">
                    {broadcast?.templateId?.category ? (
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold shadow-sm border border-black/5 ${
                          CATEGORY_COLORS[broadcast?.templateId?.category] ||
                          'bg-gray-100 text-gray-800'
                        }`}
                      >
                        {broadcast?.templateId?.category
                          .charAt(0)
                          .toUpperCase() +
                          broadcast?.templateId?.category
                            .slice(1)
                            .toLowerCase()}
                      </span>
                    ) : (
                      <span className="text-gray-400 text-xs">-</span>
                    )}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Created At
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900">
                    {formatDate(broadcast.createdAt)}
                  </dd>
                </div>

                {broadcast.scheduledAt && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">
                      Scheduled At
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {formatDate(broadcast.scheduledAt)}
                    </dd>
                  </div>
                )}

                {broadcast.executedAt && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">
                      Executed At
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {formatDate(broadcast.executedAt)}
                    </dd>
                  </div>
                )}

                {broadcast.completedAt && (
                  <div>
                    <dt className="text-sm font-medium text-gray-500">
                      Completed At
                    </dt>
                    <dd className="mt-1 text-sm text-gray-900">
                      {formatDate(broadcast.completedAt)}
                    </dd>
                  </div>
                )}

                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Contact Selection Method
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 capitalize">
                    {broadcast.contactSelectionMethod || '-'}
                  </dd>
                </div>

                <div>
                  <dt className="text-sm font-medium text-gray-500">
                    Total Cost Incurred
                  </dt>
                  <dd className="mt-1 text-sm text-gray-900 capitalize">
                    {`₹ ${broadcast.totalCostIncurred || 0}`}
                  </dd>
                </div>
              </dl>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'recipients' && (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          {/* Recipients tab header: filters + actions */}
          <div className="px-6 py-4 border-b border-gray-200">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <h3 className="text-lg font-medium text-gray-900">Recipients</h3>

              <div className="flex flex-wrap items-center gap-2">
                {/* Status filter */}
                <select
                  value={recipientStatusFilter}
                  onChange={handleFilterChange(setRecipientStatusFilter)}
                  className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">All Statuses</option>
                  <option value="pending">Pending</option>
                  <option value="queued">Queued</option>
                  <option value="sent">Sent</option>
                  <option value="delivered">Delivered</option>
                  <option value="read">Read</option>
                  <option value="failed">Failed</option>
                  <option value="bounced">Bounced</option>
                </select>

                {/* Pricing type filter */}
                <select
                  value={recipientPricingFilter}
                  onChange={handleFilterChange(setRecipientPricingFilter)}
                  className="text-sm border border-gray-300 rounded-md px-3 py-1.5 bg-white text-gray-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
                >
                  <option value="">All Pricing Types</option>
                  <option value="regular">Regular</option>
                  <option value="free_customer_service">
                    Free Customer Service
                  </option>
                </select>

                {/* Export CSV button */}
                <button
                  disabled={isExporting}
                  onClick={async () => {
                    try {
                      setIsExporting(true);
                      const blob = await broadcastsService.exportBroadcastRecipients(
                        broadcastId,
                        {
                          format: 'csv',
                          status: recipientStatusFilter || undefined,
                          pricingType: recipientPricingFilter || undefined,
                        }
                      );
                      const url = window.URL.createObjectURL(
                        new Blob([blob], { type: 'text/csv' })
                      );
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `recipients_${broadcastId}_${Date.now()}.csv`;
                      a.click();
                      window.URL.revokeObjectURL(url);
                    } catch (err) {
                      console.error('Export failed', err);
                    } finally {
                      setIsExporting(false);
                    }
                  }}
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-md border border-gray-300 text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                >
                  <svg
                    className="w-4 h-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 16v1a2 2 0 002 2h12a2 2 0 002-2v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                    />
                  </svg>
                  {isExporting ? 'Exporting…' : 'Export CSV'}
                </button>

                <button
                  onClick={() => refetchRecipients()}
                  className="text-primary-600 hover:text-primary-700 text-sm font-medium"
                >
                  Refresh
                </button>
              </div>
            </div>
          </div>

          {recipientsLoading ? (
            <div className="px-6 py-12 text-center">
              <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
              <p className="mt-2 text-sm text-gray-500">
                Loading recipients...
              </p>
            </div>
          ) : recipients.length > 0 ? (
            <div className="flex flex-col">
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Contact Info
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Activity Timestamps
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Details
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {recipients.map((recipient, index) => (
                      <tr
                        key={recipient._id || index}
                        className="hover:bg-gray-50"
                      >
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm font-medium text-gray-900">
                            {recipient.name || 'Unknown'}
                            {recipient?.pricing?.billable &&
                            recipient?.status !== 'failed' ? (
                              <span className="ml-2 text-xs text-orange-600 bg-orange-200 px-2 py-0.5 rounded-full">
                                Billable
                              </span>
                            ) : !recipient?.pricing?.billable &&
                              recipient?.status !== 'failed' ? (
                              <span className="ml-2 text-xs text-green-600 bg-green-200 px-2 py-0.5 rounded-full">
                                Free Customer Service
                              </span>
                            ) : (
                              <span className="ml-2 text-xs text-gray-600 bg-gray-200 px-2 py-0.5 rounded-full">
                                Not Billable
                              </span>
                            )}
                          </div>
                          <div className="text-sm text-gray-500 py-1">
                            {recipient.phoneNumber || '-'}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <RecipientStatusBadge status={recipient.status} />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-xs text-gray-500 space-y-1">
                            {(() => {
                              const getHistoryTimestamp = status => {
                                const entry = recipient.statusHistory?.find(
                                  h => h.status === status
                                );
                                return entry ? entry.timestamp : null;
                              };

                              const sentTime = getHistoryTimestamp('sent');
                              const deliveredTime =
                                getHistoryTimestamp('delivered');
                              const readTime = getHistoryTimestamp('read');
                              const failedTime = getHistoryTimestamp('failed');

                              return (
                                <>
                                  {sentTime && (
                                    <div className="flex">
                                      <span className="mr-2">Sent:</span>
                                      <span>{formatDate(sentTime)}</span>
                                    </div>
                                  )}
                                  {deliveredTime && (
                                    <div className="flex text-green-600">
                                      <span className="mr-2">Delivered:</span>
                                      <span>{formatDate(deliveredTime)}</span>
                                    </div>
                                  )}
                                  {readTime && (
                                    <div className="flex text-purple-600">
                                      <span className="mr-2">Read:</span>
                                      <span>{formatDate(readTime)}</span>
                                    </div>
                                  )}
                                  {failedTime && (
                                    <div className="flex text-red-600">
                                      <span className="mr-2">Failed:</span>
                                      <span>{formatDate(failedTime)}</span>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {recipient.status === 'failed' ? (
                            <div
                              className="text-xs text-red-600 bg-red-50 p-2 rounded max-w-xs truncate"
                              title={
                                recipient.failureReason ||
                                recipient.errorMessage
                              }
                            >
                              {recipient.failureReason ||
                                recipient.errorMessage ||
                                'Unknown error'}
                            </div>
                          ) : (
                            <span className="text-xs text-gray-400">
                              {recipient.messageId
                                ? `ID: ${recipient.messageId.substring(0, 10)}...`
                                : '-'}
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Recipients Pagination */}
              {recipientsPagination.totalPages > 1 && (
                <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                  <div className="flex-1 flex justify-between sm:hidden">
                    <button
                      onClick={() => setRecipientPage(p => Math.max(1, p - 1))}
                      disabled={recipientPage === 1}
                      className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Previous
                    </button>
                    <button
                      onClick={() =>
                        setRecipientPage(p =>
                          Math.min(recipientsPagination.totalPages, p + 1)
                        )
                      }
                      disabled={
                        recipientPage === recipientsPagination.totalPages
                      }
                      className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50"
                    >
                      Next
                    </button>
                  </div>
                  <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                    <div>
                      <p className="text-sm text-gray-700">
                        Showing{' '}
                        <span className="font-medium">
                          {(recipientsPagination.page - 1) * recipientLimit + 1}
                        </span>{' '}
                        to{' '}
                        <span className="font-medium">
                          {Math.min(
                            recipientsPagination.page * recipientLimit,
                            recipientsPagination.total
                          )}
                        </span>{' '}
                        of{' '}
                        <span className="font-medium">
                          {recipientsPagination.total}
                        </span>{' '}
                        recipients
                      </p>
                    </div>
                    <div>
                      <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                        <button
                          onClick={() =>
                            setRecipientPage(p => Math.max(1, p - 1))
                          }
                          disabled={recipientPage === 1}
                          className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          <span className="sr-only">Previous</span>
                          <svg
                            className="h-5 w-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                        <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                          {recipientPage} / {recipientsPagination.totalPages}
                        </span>
                        <button
                          onClick={() =>
                            setRecipientPage(p =>
                              Math.min(recipientsPagination.totalPages, p + 1)
                            )
                          }
                          disabled={
                            recipientPage === recipientsPagination.totalPages
                          }
                          className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50"
                        >
                          <span className="sr-only">Next</span>
                          <svg
                            className="h-5 w-5"
                            fill="currentColor"
                            viewBox="0 0 20 20"
                          >
                            <path
                              fillRule="evenodd"
                              d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        </button>
                      </nav>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="px-6 py-12 text-center text-sm text-gray-500">
              No recipients found or broadcast hasn't started yet.
            </div>
          )}
        </div>
      )}

      {activeTab === 'analytics' && (
        <div className="space-y-6">
          <div className="bg-white rounded-lg shadow p-6">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Analytics
            </h3>
            {analyticsLoading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                <p className="mt-2 text-sm text-gray-500">
                  Loading analytics...
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                <div className="text-center p-6 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-600">
                    Delivery Rate
                  </p>
                  <p className="mt-2 text-3xl font-bold text-gray-900">
                    {calculatePercentage(stats.delivered, totalRecipients)}%
                  </p>
                </div>

                <div className="text-center p-6 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-600">Read Rate</p>
                  <p className="mt-2 text-3xl font-bold text-gray-900">
                    {calculatePercentage(stats.read, stats.delivered)}%
                  </p>
                </div>

                <div className="text-center p-6 bg-gray-50 rounded-lg">
                  <p className="text-sm font-medium text-gray-600">
                    Reply Rate
                  </p>
                  <p className="mt-2 text-3xl font-bold text-gray-900">
                    {calculatePercentage(stats.replied, stats.delivered)}%
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Dialogs */}
      {showPauseDialog && (
        <ConfirmDialog
          title="Pause Broadcast"
          message="Are you sure you want to pause this broadcast? You can resume it later."
          confirmLabel="Pause"
          cancelLabel="Cancel"
          onConfirm={() => pauseMutation.mutate()}
          onCancel={() => setShowPauseDialog(false)}
          isLoading={pauseMutation.isPending}
        />
      )}

      {showResumeDialog && (
        <ConfirmDialog
          title="Resume Broadcast"
          message="Are you sure you want to resume this broadcast?"
          confirmLabel="Resume"
          cancelLabel="Cancel"
          onConfirm={() => resumeMutation.mutate()}
          onCancel={() => setShowResumeDialog(false)}
          isLoading={resumeMutation.isPending}
        />
      )}

      {showDeleteDialog && (
        <ConfirmDialog
          title="Delete Broadcast"
          message={`Are you sure you want to delete "${broadcast.name}"? This action cannot be undone.`}
          confirmLabel="Delete"
          cancelLabel="Cancel"
          onConfirm={() => deleteMutation.mutate()}
          onCancel={() => setShowDeleteDialog(false)}
          isLoading={deleteMutation.isPending}
        />
      )}
    </div>
  );
};

export default BroadcastDetail;
